import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { Router } from "@angular/router";
import { catchError, of, take } from "rxjs";

import { BillingRecordSummary, BillingStatus, Organization } from "../models/organizations.entity";
import { OrganizationRepository } from "../repositories/organization.repository";
import { ManagedOfferCheckoutService } from "../services/managed-offer-checkout.service";
import { getOfferContextSnapshot } from "../utils/plan-offer-marketing";

type OfferBannerStatusTone = "active" | "pending" | "legacy" | "empty";

@Component({
  selector: "app-organization-offer-banner",
  templateUrl: "./organization-offer-banner.component.html",
  styleUrls: ["./organization-offer-banner.component.scss"],
})
export class OrganizationOfferBannerComponent implements OnChanges {
  @Input() organization?: Organization;

  billingStatus: BillingStatus | null = null;
  loadingStatus = false;

  constructor(
    private readonly router: Router,
    private readonly organizationRepository: OrganizationRepository,
    private readonly managedOfferCheckoutService: ManagedOfferCheckoutService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes["organization"]) {
      return;
    }

    const organizationId = this.organization?.id;
    if (!organizationId) {
      this.billingStatus = null;
      return;
    }

    this.loadingStatus = true;
    this.organizationRepository.getBillingStatus(organizationId)
      .pipe(
        take(1),
        catchError(() => of(null)),
      )
      .subscribe((billingStatus) => {
        this.billingStatus = billingStatus;
        this.loadingStatus = false;
      });
  }

  get currentOfferLabel(): string {
    const snapshot = getOfferContextSnapshot(this.organization);
    return snapshot.currentPlanLabel ?? "Sans offre active";
  }

  get currentOfferTitle(): string {
    if (this.organization?.activeSubscription || this.isLegacyOffer) {
      return this.currentOfferLabel;
    }

    if (this.pendingPaymentRecord) {
      return "Activation en attente";
    }

    return "Offre à activer";
  }

  get currentOfferDescription(): string {
    if (this.pendingPaymentRecord) {
      const amountLabel = this.pendingPaymentAmountLabel ? ` pour ${this.pendingPaymentAmountLabel}` : "";
      return `Un paiement est déjà prêt${amountLabel}. Reprenez ce flux ou finalisez l’activation depuis Crédits et offre.`;
    }

    if (this.organization?.activeSubscription) {
      return "L’organisation garde ici un repère clair sur son offre, son cycle et son nombre de collaborateurs.";
    }

    if (this.isLegacyOffer) {
      return "Cette organisation est encore rattachée à une formule historique. Comparez les offres 2026 ou ouvrez Crédits et offre pour préparer la suite.";
    }

    return "Cette organisation n’a pas encore d’offre active. Utilisez ce repère pour lancer l’activation au bon moment.";
  }

  get statusLabel(): string {
    if (this.pendingPaymentRecord) {
      return "Paiement en attente";
    }

    if (this.organization?.activeSubscription) {
      return "Offre active";
    }

    if (this.organization?.subscriptionPlan?.slug || this.organization?.subscriptionPlan?.name) {
      return "Formule historique";
    }

    return "Sans offre active";
  }

  get statusTone(): OfferBannerStatusTone {
    if (this.pendingPaymentRecord) {
      return "pending";
    }

    if (this.organization?.activeSubscription) {
      return "active";
    }

    if (this.organization?.subscriptionPlan?.slug || this.organization?.subscriptionPlan?.name) {
      return "legacy";
    }

    return "empty";
  }

  get isLegacyOffer(): boolean {
    return !!(this.organization?.subscriptionPlan?.slug || this.organization?.subscriptionPlan?.name)
      && !this.organization?.activeSubscription;
  }

  get nextRenewalLabel(): string {
    if (this.organization?.activeSubscription?.nextRenewalAt) {
      return this.formatDate(this.organization.activeSubscription.nextRenewalAt);
    }

    if (this.billingStatus?.nextRenewalAt) {
      return this.formatDate(this.billingStatus.nextRenewalAt);
    }

    return "Non planifié";
  }

  get seatCountLabel(): string {
    const seatCount = this.organization?.activeSubscription?.seatCount;
    if (!seatCount) {
      return "Non défini";
    }

    return `${seatCount} collaborateur${seatCount > 1 ? "s" : ""}`;
  }

  get journeysRemainingLabel(): string {
    const totalJourneys =
      (this.organization?.creditSummary?.includedJourneysRemaining ?? 0)
      + (this.organization?.creditSummary?.complementaryJourneysRemaining ?? 0);

    return `${totalJourneys}`;
  }

  get pendingPaymentRecord(): BillingRecordSummary | null {
    return this.billingStatus?.pendingRenewalCheckout
      ?? this.billingStatus?.pendingSubscriptionCheckout
      ?? this.billingStatus?.lastComplementaryJourneysCheckout
      ?? null;
  }

  get pendingPaymentAmountLabel(): string | null {
    return this.pendingPaymentRecord?.amountCents
      ? this.managedOfferCheckoutService.formatCurrency(this.pendingPaymentRecord.amountCents)
      : null;
  }

  get isUnitsPage(): boolean {
    return this.router.url.includes("/units");
  }

  goToUnits(): void {
    if (!this.organization?.id || this.isUnitsPage) {
      return;
    }

    this.router.navigate(["organizations", this.organization.id, "units"]);
  }

  goToCompanyOffers(): void {
    this.router.navigateByUrl("/offres-entreprises-2026");
  }

  private formatDate(date?: string | Date | null): string {
    if (!date) {
      return "Non disponible";
    }

    return new Date(date).toLocaleDateString();
  }
}
